import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  orderBy,
  query,
  where,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { XR_EXPERIENCES } from '../data/xr-experiences';

const SETTINGS_PATH = 'settings/app';
const EXPERIENCES_COLLECTION = 'experiences';
const THUMBNAILS_PREFIX = 'thumbnails';

/**
 * Fetch app settings (who can post). Used for UI and guard.
 * @returns {{ allowAnyoneToPost: boolean, allowedUploaderUids: string[] }}
 */
export async function getPostSettings() {
  const snap = await getDoc(doc(db, SETTINGS_PATH));
  if (!snap.exists()) {
    return { allowAnyoneToPost: false, allowedUploaderUids: [], reviewerUids: [] };
  }
  const data = snap.data();
  return {
    allowAnyoneToPost: !!data.allowAnyoneToPost,
    allowedUploaderUids: Array.isArray(data.allowedUploaderUids) ? data.allowedUploaderUids : [],
    reviewerUids: Array.isArray(data.reviewerUids) ? data.reviewerUids : []
  };
}

/**
 * Whether the given user (by uid) is allowed to create experiences.
 * Any signed-in user can submit; moderators approve before publish.
 */
export async function canUserCreatePost(userUid) {
  return !!userUid;
}

/**
 * Whether the given user can access the review screen and approve content.
 */
export async function canUserReview(userUid) {
  if (!userUid) return false;
  const { reviewerUids } = await getPostSettings();
  return reviewerUids.includes(userUid);
}

function mapDocToExperience(d) {
  const data = d.data();
  return {
    id: d.id,
    title: data.title ?? '',
    category: data.category ?? '360-video',
    categoryChip: data.categoryChip ?? 'Experience',
    description: data.description ?? '',
    thumbnail: data.thumbnail ?? '',
    url: data.url ?? '',
    duration: data.duration ?? '',
    locationCountry: data.locationCountry ?? '',
    locationCity: data.locationCity ?? '',
    tags: Array.isArray(data.tags) ? data.tags : [],
    contentType: data.contentType ?? 'movies',
    medium: data.medium ?? 'metaverse',
    isLBE: !!data.isLBE,
    launchInBrowser: !!data.launchInBrowser,
    supportedDevices: Array.isArray(data.supportedDevices) ? data.supportedDevices : ['browser'],
    comfortRating: data.comfortRating ?? null,
    ageRating: data.ageRating ?? null,
    status: data.status ?? 'approved',
    createdBy: data.createdBy ?? null,
    createdByEmail: data.createdByEmail ?? null,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toMillis() : data.createdAt,
    // Extended optional schema fields (pass through when present)
    slug: data.slug ?? null,
    creatorName: data.creatorName ?? null,
    creatorId: data.creatorId ?? null,
    creatorVerified: data.creatorVerified ?? false,
    creatorExperienceCount: data.creatorExperienceCount ?? null,
    creatorBio: data.creatorBio ?? null,
    creatorLogoUrl: data.creatorLogoUrl ?? null,
    primaryFormat: data.primaryFormat ?? null,
    formatBadge: data.formatBadge ?? null,
    formatDescriptor: data.formatDescriptor ?? null,
    venueModel: data.venueModel ?? null,
    realityCheckPreview: data.realityCheckPreview ?? null,
    officialDescription: data.officialDescription ?? null,
    heroImageUrl: data.heroImageUrl ?? null,
    trailerUrl: data.trailerUrl ?? null,
    galleryUrls: Array.isArray(data.galleryUrls) ? data.galleryUrls : [],
    platforms: Array.isArray(data.platforms) ? data.platforms : [],
    devices: Array.isArray(data.devices) ? data.devices : [],
    priceType: data.priceType ?? null,
    priceLabel: data.priceLabel ?? null,
    languages: Array.isArray(data.languages) ? data.languages : [],
    sessionStructure: data.sessionStructure ?? null,
    typicalSessionLabel: data.typicalSessionLabel ?? null,
    runtimeMinutes: data.runtimeMinutes ?? null,
    recommendedVisitTime: data.recommendedVisitTime ?? null,
    groupSize: Array.isArray(data.groupSize) ? data.groupSize : [],
    multiplayer: data.multiplayer ?? null,
    bestFor: Array.isArray(data.bestFor) ? data.bestFor : [],
    effortSignal: data.effortSignal ?? null,
    availabilityType: data.availabilityType ?? null,
    availabilityStatus: data.availabilityStatus ?? null,
    availabilityStatusLabel: data.availabilityStatusLabel ?? null,
    venueName: data.venueName ?? null,
    venueAddress: data.venueAddress ?? null,
    venueCity: data.venueCity ?? null,
    venueCountry: data.venueCountry ?? null,
    venueLat: data.venueLat ?? null,
    venueLng: data.venueLng ?? null,
    venueHours: data.venueHours ?? null,
    venueParking: data.venueParking ?? null,
    venueTransit: data.venueTransit ?? null,
    venueAccessibilityEntrance: data.venueAccessibilityEntrance ?? null,
    comePrepared: Array.isArray(data.comePrepared) ? data.comePrepared : [],
    locomotionTypes: Array.isArray(data.locomotionTypes) ? data.locomotionTypes : [],
    contentNotes: Array.isArray(data.contentNotes) ? data.contentNotes : [],
    accessibilityFeatures: Array.isArray(data.accessibilityFeatures) ? data.accessibilityFeatures : [],
    physicalRequirements: Array.isArray(data.physicalRequirements) ? data.physicalRequirements : [],
    verifiedBy: data.verifiedBy ?? null,
    lastUpdated: data.lastUpdated ?? null,
    editorialBadge: data.editorialBadge ?? null,
    editorialBadgeDetail: data.editorialBadgeDetail ?? null,
    partOfCollection: data.partOfCollection ?? null,
    tourStops: Array.isArray(data.tourStops) ? data.tourStops : []
  };
}

function mapDocToOccurrence(d) {
  const data = d.data();
  return {
    id: d.id,
    experienceId: data.experienceId ?? '',
    title: data.title ?? '',
    startDatetime: data.startDatetime instanceof Timestamp ? data.startDatetime.toDate() : data.startDatetime,
    endDatetime: data.endDatetime instanceof Timestamp ? data.endDatetime.toDate() : data.endDatetime,
    timezone: data.timezone ?? '',
    venueName: data.venueName ?? '',
    venueCity: data.venueCity ?? '',
    venueLat: data.venueLat ?? null,
    venueLng: data.venueLng ?? null,
    ticketUrl: data.ticketUrl ?? '',
    priceLabel: data.priceLabel ?? '',
    status: data.status ?? 'available',
    notes: data.notes ?? ''
  };
}

/**
 * Fetch experiences that are approved (for public feed).
 */
export async function fetchExperiencesFromFirestore() {
  const q = query(
    collection(db, EXPERIENCES_COLLECTION),
    where('status', '==', 'approved'),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => mapDocToExperience(d));
}

/**
 * Upload thumbnail file to Storage and return public URL.
 */
async function uploadThumbnail(file, experienceId, uploaderUid) {
  const ext = (file.name && file.name.split('.').pop()) || 'jpg';
  const path = `${THUMBNAILS_PREFIX}/${experienceId}.${ext}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, {
    contentType: file.type || 'image/jpeg',
    customMetadata: { uploaderUid },
  });
  return getDownloadURL(storageRef);
}

/**
 * Create a new experience: upload thumbnail (if provided) then create Firestore doc.
 * Requires user to be allowed (check canUserCreatePost first).
 * @param {Object} data - experience fields (title, category, description, url, etc.)
 * @param {File|null} thumbnailFile - optional image file
 * @param {string} userUid - current user uid
 * @returns {{ id: string }} the new experience doc id
 */
/**
 * @param {string} [userEmail] - creator email (for notifications when approved)
 */
export async function createExperience(data, thumbnailFile, userUid, userEmail) {
  if (!userUid) throw new Error('You must be signed in to create a post.');

  const docRef = doc(collection(db, EXPERIENCES_COLLECTION));
  const experienceId = docRef.id;

  let thumbnailUrl = typeof data.thumbnail === 'string' ? data.thumbnail : '';
  if (thumbnailFile && thumbnailFile.size > 0) {
    thumbnailUrl = await uploadThumbnail(thumbnailFile, experienceId, userUid);
  }

  const payload = {
    title: String(data.title ?? '').trim(),
    category: data.category ?? '360-video',
    categoryChip: data.categoryChip ?? 'Experience',
    description: String(data.description ?? '').trim(),
    thumbnail: thumbnailUrl,
    url: String(data.url ?? '').trim(),
    duration: String(data.duration ?? '').trim(),
    tags: Array.isArray(data.tags) ? data.tags : [],
    contentType: data.contentType ?? 'movies',
    medium: data.medium ?? 'metaverse',
    isLBE: !!data.isLBE,
    launchInBrowser: !!data.launchInBrowser,
    supportedDevices: Array.isArray(data.supportedDevices) ? data.supportedDevices : ['browser'],
    comfortRating: data.comfortRating ?? 'comfortable',
    ageRating: data.ageRating ?? 'all',
    status: 'pending',
    createdBy: userUid,
    createdByEmail: userEmail || null,
    createdAt: serverTimestamp()
  };

  await setDoc(doc(db, EXPERIENCES_COLLECTION, experienceId), payload);
  return { id: experienceId };
}

/**
 * Fetch experiences created by the given user (for "My uploads").
 */
export async function fetchMyUploads(userUid) {
  const q = query(
    collection(db, EXPERIENCES_COLLECTION),
    where('createdBy', '==', userUid),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(mapDocToExperience);
}

/**
 * Fetch experiences with status pending (for review screen). Requires reviewer permission (enforced by rules).
 */
export async function fetchPendingExperiences() {
  const q = query(
    collection(db, EXPERIENCES_COLLECTION),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(mapDocToExperience);
}

/**
 * Creator interest form entries (while full creator submission is gated).
 * Reviewers only — enforced by Firestore rules.
 */
/**
 * Creator and user share-flow submissions waiting for review.
 * Reviewers only — enforced by Firestore rules.
 */
export async function fetchPendingSubmissions(limitCount = 50) {
  const q = query(
    collection(db, 'submissions'),
    where('status', '==', 'pending_review'),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    const createdAt = data.createdAt?.toDate?.() ?? null;
    return {
      id: docSnap.id,
      type: data.type || '',
      title: data.title || data.experienceName || 'Untitled',
      creatorName: data.creatorDisplayName || data.submittedByDisplayName || '',
      email: data.submittedByEmail || '',
      link: data.primaryExperienceLink || data.url || '',
      notes: data.notes || '',
      heroImageUrl: data.heroImageUrl || '',
      status: data.status || '',
      createdAt,
    };
  });
}

/**
 * Creator interest form entries (while full creator submission is gated).
 * Reviewers only — enforced by Firestore rules.
 */
export async function fetchCreatorWaitlist(limitCount = 100) {
  const q = query(
    collection(db, 'creator_waitlist'),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    const createdAt = data.createdAt?.toDate?.() ?? null;
    return {
      id: docSnap.id,
      name: data.name || '',
      email: data.email || '',
      experienceUrl: data.experienceUrl || '',
      submittedByUid: data.submittedByUid || null,
      createdAt,
    };
  });
}

/**
 * Approve an experience. Caller must be in reviewerUids (Firestore rules enforce).
 */
export async function approveExperience(experienceId) {
  const ref = doc(db, EXPERIENCES_COLLECTION, experienceId);
  await updateDoc(ref, { status: 'approved' });
}

/**
 * One-time migration: write all static XR_EXPERIENCES to Firestore.
 * Only allowed uploaders can run this. Uses existing thumbnail URLs (no upload).
 * Merges into existing docs so existing Firestore data is not wiped.
 */
export async function seedExperiencesToFirestore(userUid) {
  if (!userUid) throw new Error('You must be signed in to migrate content.');
  const canReview = await canUserReview(userUid);
  if (!canReview) throw new Error('You are not authorized to migrate content.');

  for (const exp of XR_EXPERIENCES) {
    const payload = {
      title: exp.title ?? '',
      category: exp.category ?? '360-video',
      categoryChip: exp.categoryChip ?? 'Experience',
      description: exp.description ?? '',
      thumbnail: typeof exp.thumbnail === 'string' ? exp.thumbnail : '',
      url: exp.url ?? '',
      duration: exp.duration ?? '',
      tags: Array.isArray(exp.tags) ? exp.tags : [],
      contentType: exp.contentType ?? 'movies',
      medium: exp.medium ?? 'metaverse',
      isLBE: !!exp.isLBE,
      launchInBrowser: !!exp.launchInBrowser,
      supportedDevices: Array.isArray(exp.supportedDevices) ? exp.supportedDevices : ['browser'],
      comfortRating: exp.comfortRating ?? 'comfortable',
      ageRating: exp.ageRating ?? 'all',
      status: 'approved',
      createdBy: userUid,
      createdAt: serverTimestamp()
    };
    await setDoc(doc(db, EXPERIENCES_COLLECTION, exp.id), payload, { merge: true });
  }

  return { count: XR_EXPERIENCES.length };
}

const OCCURRENCES_COLLECTION = 'occurrences';

/**
 * Fetch upcoming occurrences for a scheduled experience.
 * Returns [] if collection is empty or query fails (e.g. missing index).
 */
export async function fetchOccurrencesForExperience(experienceId, limitCount = 10) {
  if (!experienceId) return [];
  try {
    const now = Timestamp.now();
    const q = query(
      collection(db, OCCURRENCES_COLLECTION),
      where('experienceId', '==', experienceId),
      where('startDatetime', '>=', now),
      orderBy('startDatetime', 'asc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(mapDocToOccurrence);
  } catch (_) {
    return [];
  }
}
